import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ActivityScreen from "../../../../components/screens/Activity/ActivityScreen";
import {
  courses as courseQuerys,
  completions as completionQuerys,
  progress as progressQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { DateTime } from "luxon";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { getSectionProgress } from "@nualang/nualang-api-and-queries/APIs/Progress";
import { getSectionCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import {
  completions as completionHelpers,
  progress as progressHelpers,
  courses as courseHelpers,
} from "../../../../utils/helpers";
import config from "../../../../config";

function Section(props) {
  const [title, setTitle] = useState("");
  const [picture, setPicture] = useState("");
  const [description, setDescription] = useState("");
  const [filterName, setFilterName] = useState("");
  const [fromDate, setFromDate] = React.useState(
    DateTime.now().minus({ years: 1 }),
  );
  const [toDate, setToDate] = React.useState(DateTime.now());

  const {
    authData: { username, attributes },
    handleViewExerciseAttempt,
  } = props;
  const params = useParams();
  const { courseId, sectionId } = params;

  const { t } = useTranslation();

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const isCreator = course.createdBy === username;

  const sectionsQuery = courseQuerys.useCourseSections(
    async (courseId, filters) => {
      const sections = await getCourseSections(courseId, filters);
      return sections;
    },
    {
      courseId,
      username: props.authData.username,
      filters: {
        limit: config.limits.courseSections,
      },
    },
    {
      enabled: !!courseId,
    },
  );

  const courseSections = useMemo(
    () =>
      sectionsQuery.isSuccess &&
      Array.isArray(sectionsQuery.data.Items) &&
      sectionsQuery.data.Items.length
        ? sectionsQuery.data.Items
        : [],
    [sectionsQuery.data, sectionsQuery.isSuccess],
  );

  const sectionProgressQuery = progressQuerys.useSectionProgress(
    async () => {
      const sectionProgress = await getSectionProgress(courseId, sectionId);
      let formattedProgress = await progressHelpers.formatProgress(
        sectionProgress.Items.filter(
          (item) => isCreator || item.memberId === username,
        ),
        username,
        attributes.preferred_username,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      return formattedProgress;
    },
    {
      courseId,
      sectionId,
      filters: {
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!(courseId && sectionId) && courseQuery.isSuccess },
  );

  const sectionProgress =
    sectionProgressQuery.isSuccess &&
    sectionProgressQuery.data &&
    sectionProgressQuery.data.length
      ? sectionProgressQuery.data
      : [];

  const sectionCompletionsQuery = completionQuerys.useSectionCompletions(
    async () => {
      const sectionCompletions = await getSectionCompletions(
        courseId,
        sectionId,
      );
      let formattedCompletions = await completionHelpers.formatCompletions(
        sectionCompletions.Items.filter(
          (item) => isCreator || item.memberId === username,
        ),
        username,
        attributes.preferred_username,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedCompletions = formattedCompletions.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedCompletions = formattedCompletions.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      return formattedCompletions;
    },
    {
      courseId,
      sectionId,
      filters: {
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!(courseId && sectionId) && courseQuery.isSuccess },
  );

  const sectionCompletions =
    sectionCompletionsQuery.isSuccess &&
    sectionCompletionsQuery.data &&
    sectionCompletionsQuery.data.length
      ? sectionCompletionsQuery.data
      : [];

  const fromDateChange = (date) => {
    setFromDate(date);
  };

  const toDateChange = (date) => {
    setToDate(date);
  };

  const filterChange = (filter) => {
    setFilterName(filter);
  };

  const completions = sectionCompletions;

  useEffect(() => {
    if (course?.picture && courseSections && course?.description) {
      setPicture(course?.picture);
      setDescription(course?.description);

      const section = courseSections.find((s) => s.sectionId === sectionId);
      setTitle(section.sectionName);
    }
  }, [sectionId, course?.picture, courseSections, course?.description]);

  return (
    <ActivityScreen
      t={t}
      filterChange={filterChange}
      fromDate={fromDate}
      toDate={toDate}
      toDateChange={toDateChange}
      fromDateChange={fromDateChange}
      isCreator={isCreator}
      completions={completions || []}
      completionsTotal={(completions || []).length}
      progress={sectionProgress || []}
      progressTotal={(sectionProgress || []).length}
      handleViewEvent={handleViewExerciseAttempt}
      title={title}
      picture={picture}
      description={description}
      isCompletionsLoading={sectionCompletionsQuery.isLoading}
      isProgressLoading={sectionProgressQuery.isLoading}
      mainSiteUrl={config.SITE_URL}
    />
  );
}

Section.propTypes = {
  authData: PropTypes.object,
  themeContext: PropTypes.object,
  handleViewExerciseAttempt: PropTypes.func,
};

export default Section;
